import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo } from "react";
import { z } from "zod";

import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { studentReportQueryOptions } from "@/hooks/query/dashboard";
import { deriveAttemptInsights } from "@/routes/learn/-components/attempt/attempt-utils";

import { StudentHeader } from "./-components/reports/student-header";
import { StudentStatCards } from "./-components/reports/student-stat-cards";
import { VersionAttemptsTable } from "./-components/reports/version-attempts-table";

export const Route = createFileRoute("/(app)/dashboard/reports")({
	validateSearch: z.object({
		studentId: z.string(),
	}),
	beforeLoad: ({ context, matches }) => {
		const user = context.user;
		const pathname = matches
			.map((match) => match.pathname.slice(0, match.pathname.length - 1))
			.join("");

		if (!user || user.isAnonymous) {
			throw redirect({ to: "/sign-in", search: { from: pathname } });
		}
	},
	component: StudentReportPage,
});

function StudentReportPage() {
	const { studentId } = Route.useSearch();
	const { data, isPending } = useQuery(studentReportQueryOptions(studentId));

	const insights = useMemo(() => {
		if (!data?.attempts) return [];
		return data.attempts
			.map((a) => deriveAttemptInsights(a.engineState))
			.filter((i): i is NonNullable<typeof i> => i !== null);
	}, [data?.attempts]);

	const completedAttempts = data?.attempts.filter((a) => a.status === "completed").length ?? 0;

	if (isPending) {
		return (
			<div className="space-y-6 p-6">
				<Skeleton className="h-12 w-64" />
				<Skeleton className="h-6 w-48" />
				<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-24 w-full" />
					))}
				</div>
				<Skeleton className="h-64 w-full" />
			</div>
		);
	}

	if (!data) {
		return null;
	}

	return (
		<div className="space-y-6 p-6">
			<StudentHeader student={data.student} />
			<Separator />
			<StudentStatCards
				insights={insights}
				totalAttempts={data.attempts.length}
				completedAttempts={completedAttempts}
			/>
			<VersionAttemptsTable attempts={data.attempts} />
		</div>
	);
}
